/** One page of a long collection, and the way to the next. */

import type { CSSProperties } from "react";

import { cn } from "@/lib/cn";

import { Button } from "../Button/Button";
import "./Pagination.css";

export interface PaginationProps {
  /** 1-indexed, like every position in this product. */
  readonly page: number;
  readonly pageCount: number;
  readonly pageSize?: number;
  readonly total?: number;
  /** Names what is being paged, for the landmark. */
  readonly label?: string;
  readonly onChange?: (page: number) => void;
  readonly className?: string;
  readonly style?: CSSProperties;
}

export function Pagination({
  page,
  pageCount,
  pageSize,
  total,
  label = "Pages",
  onChange,
  className,
  style,
}: PaginationProps) {
  const first = pageSize === undefined ? undefined : (page - 1) * pageSize + 1;
  const last =
    pageSize === undefined || total === undefined
      ? undefined
      : Math.min(page * pageSize, total);

  return (
    <nav aria-label={label} className={cn("al-pagination", className)} style={style}>
      <Button
        size="sm"
        variant="ghost"
        disabled={page <= 1}
        onClick={() => onChange?.(page - 1)}
      >
        Previous
      </Button>
      <span className="al-pagination-position al-tabular" aria-live="polite">
        {page} of {pageCount}
      </span>
      {first !== undefined && last !== undefined && total !== undefined ? (
        <span className="al-pagination-range al-tabular">
          {first}-{last} of {total}
        </span>
      ) : null}
      <Button
        size="sm"
        variant="ghost"
        disabled={page >= pageCount}
        onClick={() => onChange?.(page + 1)}
      >
        Next
      </Button>
    </nav>
  );
}
